import React, { useRef } from 'react';
import { UploadCloudIcon, SparklesIcon, DownloadIcon, RefreshCcwIcon, AlertTriangleIcon, Loader2Icon, WandIcon, CheckIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { Slider } from '../ui/Slider';
import { CtaLink } from '../CtaLink';
import { ApiKeyPanel } from './ApiKeyPanel';
import { BeforeAfterSlider } from './BeforeAfterSlider';
import { usePhotoTransformer } from './usePhotoTransformer';
import { roomOptions, styleOptions, colorOptions } from '../../data/interiorOptions';
import { proPerks } from '../../data/contact';
export function PhotoTransformerPlugin() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const t = usePhotoTransformer();
  const openFilePicker = () => fileInputRef.current?.click();
  return <div className="reveal bg-white rounded-3xl md:rounded-[2.5rem] shadow-xl border border-gray-200 overflow-hidden" data-id="element-214" data-mp="24syjb2k9bj2v">
      <div className="p-6 md:p-10 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4" data-id="element-215" data-mp="29pyq5foxkpv1">
        <div data-id="element-216" data-mp="1nhwvxmgkqlfn">
          <div className="flex items-center gap-2 mb-1.5" data-id="element-217" data-mp="1ikwr1evgnoq4">
            <WandIcon className="w-5 h-5 text-apple-blue" aria-hidden="true" data-id="element-218" data-mp="1ikdksvf6aorx" />
            <h2 id="demo-nadpis" className="text-2xl md:text-3xl font-bold text-apple-black" data-id="element-219" data-mp="1x8rk4wddrv3q">
              Vyskúšaj Interior AI naživo
            </h2>
          </div>
          <p className="text-apple-gray" data-id="element-220" data-mp="14kvqk0j3yjb6">Nahraj fotku, vyber štýl a nechaj AI pretvoriť tvoj priestor.</p>
        </div>
        <span className="flex items-center gap-1.5 bg-blue-50 text-apple-blue text-xs font-bold px-3 py-1.5 rounded-full" data-id="element-221" data-mp="zmu89htiao0r">
          <SparklesIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-222" data-mp="1dmlg10tfj86b" />
          Gemini 2.5 Flash Image
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-223" data-mp="19mbdxz8rd1wq">
        {/* Left: upload / preview / result */}
        <div className="p-6 md:p-10 bg-apple-light/50 border-b lg:border-b-0 lg:border-r border-gray-100 flex flex-col justify-center" data-id="element-224" data-mp="2p2tnngnpx9ou">
          <input ref={fileInputRef} id="interior-foto" type="file" accept="image/*" className="sr-only" tabIndex={-1} onChange={e => {
          t.handleFile(e.target.files?.[0] || null);
          e.target.value = '';
        }} data-id="element-225" data-mp="1hozq9gt5otke" />

          <div aria-live="polite" className="sr-only" data-id="element-226" data-mp="1vnpncwfz6t8r">
            {t.isLoading ? 'Generujem návrh interiéru.' : t.result ? 'Návrh je hotový.' : ''}
          </div>

          {!t.previewUrl && <button type="button" onClick={openFilePicker} onDragOver={e => {
          e.preventDefault();
          t.setIsDragging(true);
        }} onDragLeave={() => t.setIsDragging(false)} onDrop={t.handleDrop} className={`w-full border-2 border-dashed rounded-2xl bg-white p-8 md:p-12 flex flex-col items-center justify-center text-center transition-colors ${t.isDragging ? 'border-apple-blue bg-blue-50/50' : 'border-gray-300 hover:border-apple-blue hover:bg-blue-50/30'}`} data-id="element-227" data-mp="13xl10s8nnw4w">
              <span className="w-16 h-16 md:w-20 md:h-20 bg-blue-50 rounded-full flex items-center justify-center mb-5" data-id="element-228" data-mp="7ondmuoltgww">
                <UploadCloudIcon className="w-8 h-8 md:w-10 md:h-10 text-apple-blue" aria-hidden="true" data-id="element-229" data-mp="3uvphtd3a23og" />
              </span>
              <span className="text-lg font-semibold text-apple-black mb-1.5" data-id="element-230" data-mp="3px4q6223csbm">Nahraj fotku izby</span>
              <span className="text-sm text-apple-gray mb-6" data-id="element-231" data-mp="3l4l4six5wsw9">Potiahni sem alebo klikni · JPG, PNG · max 10 MB</span>
              <span className="inline-flex items-center min-h-[44px] px-5 rounded-full border border-gray-300 text-sm font-semibold text-apple-black bg-white" data-id="element-232" data-mp="3g6tfzxrtdmwa">
                Vybrať súbor
              </span>
            </button>}

          {t.previewUrl && !t.result && !t.isLoading && <div className="relative rounded-2xl overflow-hidden shadow-md border border-gray-100" data-id="element-233" data-mp="327ah7vkw7nwn">
              <img src={t.previewUrl} alt="Tvoja nahraná fotka izby" className="w-full aspect-[4/3] object-cover" data-id="element-234" data-mp="1645q6h1p39n5" />
              <button type="button" onClick={openFilePicker} className="absolute top-3 right-3 min-h-[44px] px-4 bg-white text-apple-black text-sm font-semibold rounded-full shadow-md hover:bg-gray-50 transition-colors" data-id="element-235" data-mp="1b49y3iojew1r">
                Zmeniť fotku
              </button>
            </div>}

          {t.previewUrl && t.isLoading && <div className="relative rounded-2xl overflow-hidden shadow-md border border-gray-100" data-id="element-236" data-mp="deorj1nhewih">
              <img src={t.previewUrl} alt="" className="w-full aspect-[4/3] object-cover opacity-40" data-id="element-237" data-mp="idry4qj41ask" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4" data-id="element-238" data-mp="27spxs7ld2lsl">
                <Loader2Icon className="w-10 h-10 text-apple-blue animate-spin" aria-hidden="true" data-id="element-239" data-mp="2crsxrdmhi1tp" />
                <span className="bg-white text-apple-black text-sm font-semibold px-4 py-2 rounded-full shadow-md" data-id="element-240" data-mp="147j96b9uuui">
                  AI navrhuje tvoj priestor…
                </span>
              </div>
            </div>}

          {t.previewUrl && t.result && !t.isLoading && <div className="space-y-3" data-id="element-241" data-mp="63k45lil1rug">
              <BeforeAfterSlider beforeSrc={t.previewUrl} afterSrc={t.result.src} data-id="element-242" data-mp="62irjclx4xam" />
              {t.result.isDemo && <p className="text-sm text-amber-700 font-medium flex items-start gap-1.5" data-id="element-243" data-mp="jtdts61wwg58">
                  <SparklesIcon className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-244" data-mp="evcbhncb8kut" />
                  Ukážkový náhľad. Pre reálnu transformáciu tvojej fotky pripoj Gemini API kľúč.
                </p>}
            </div>}
        </div>

        {/* Right: settings */}
        <div className="p-6 md:p-10 flex flex-col justify-center" data-id="element-245" data-mp="2vl1b4di3brfe">
          <h3 className="text-xl md:text-2xl font-bold text-apple-black mb-6 md:mb-8" data-id="element-246" data-mp="2qm6ikjubmoi3">Nastavenia dizajnu</h3>

          <div className="space-y-7" data-id="element-247" data-mp="3galq1ah2em47">
            <div data-id="element-248" data-mp="3gbn1vh5wncmz">
              <span className="block text-sm font-semibold text-apple-black mb-2.5" data-id="element-249" data-mp="3kb5lm9lzpho8">Typ priestoru</span>
              <Select options={roomOptions} value={t.roomType} onChange={t.setRoomType} placeholder="Vyberte typ miestnosti" data-id="element-250" data-mp="3pa8gx2c649j0" />
            </div>

            <div data-id="element-251" data-mp="3ua4lp3sow4zm">
              <span className="block text-sm font-semibold text-apple-black mb-2.5" data-id="element-252" data-mp="3uampclhollhk">Štýl interiéru</span>
              <Select options={styleOptions} value={t.style} onChange={t.setStyle} placeholder="Vyberte štýl" data-id="element-253" data-mp="32s5aqzmrvkr" />
            </div>

            <fieldset data-id="element-254" data-mp="bvc8m4qvaobx">
              <legend className="block text-sm font-semibold text-apple-black mb-3" data-id="element-255" data-mp="bv2f476m5z1h">Farebná paleta</legend>
              <div className="flex flex-wrap gap-3 md:gap-4" data-id="element-256" data-mp="gtdmmb9frxgj">
                {colorOptions.map(color => {
                const selected = t.colorId === color.id;
                return <button key={color.id} type="button" onClick={() => t.setColorId(color.id)} aria-pressed={selected} className="flex flex-col items-center gap-1.5 rounded-lg p-1" data-id="element-257" data-mp="qto2y4qrpkzx">
                      <span className={`w-11 h-11 rounded-full ${color.swatchClass} border-2 flex items-center justify-center transition-transform ${selected ? 'border-apple-blue ring-4 ring-blue-100' : 'border-gray-200'}`} data-id="element-258" data-mp="21d93u3ggfoku">
                        {selected && <CheckIcon className="w-4 h-4 text-white drop-shadow" aria-hidden="true" data-id="element-259" data-mp="2p4ehqd2u46si" />}
                      </span>
                      <span className={`text-xs font-medium ${selected ? 'text-apple-blue' : 'text-apple-gray'}`} data-id="element-260" data-mp="2fcs98ohvsltg">
                        {color.label}
                      </span>
                    </button>;
              })}
              </div>
            </fieldset>

            <div data-id="element-261" data-mp="2gdl9fotxcfbs">
              <div className="flex justify-between items-center mb-3" data-id="element-262" data-mp="2gd3482paaqrq">
                <span className="block text-sm font-semibold text-apple-black" data-id="element-263" data-mp="2l41rjm8owp4h">Odhadovaný rozpočet</span>
                <span className="text-sm font-bold text-apple-blue" data-id="element-264" data-mp="27cwy51vx0t2l">€{t.budgetValue.toLocaleString('sk-SK')}</span>
              </div>
              <div className="px-2" data-id="element-265" data-mp="2ccsxuoxkxycc">
                <Slider min={500} max={20000} step={500} value={t.budget} onChange={t.setBudget} showLabels={false} data-id="element-266" data-mp="2c9n8d8l1gug3" />
              </div>
              <div className="flex justify-between text-xs text-apple-gray mt-2" data-id="element-267" data-mp="343fma6e8u3w1">
                <span data-id="element-268" data-mp="2q3emku9pt2i5">€500</span>
                <span data-id="element-269" data-mp="2q238a0in8ccs">€20 000+</span>
              </div>
            </div>

            {t.error && <div role="alert" className="flex items-start gap-2.5 bg-red-50 border border-red-100 text-red-700 text-sm font-medium rounded-xl p-4" data-id="element-270" data-mp="2v16f9qi6osne">
                <AlertTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-271" data-mp="1eqc2hp8eput8" />
                <span data-id="element-272" data-mp="1jkpvxm3idrv9">{t.error}</span>
              </div>}

            {!t.result ? <Button size="large" onClick={t.generate} loading={t.isLoading} className="w-full !min-h-[52px] !bg-apple-black hover:!bg-gray-800 !text-white !rounded-full !font-semibold !border-none" data-id="element-273" data-mp="1c5e2y6q746me">
                Vygenerovať dizajn
              </Button> : <div className="flex flex-col sm:flex-row gap-3" data-id="element-274" data-mp="sgdk909wwt3h">
                <Button size="large" variant="secondary" onClick={t.download} leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-276" data-mp="126ybu3r64xcw" />} className="flex-1 !min-h-[48px] !rounded-full !font-semibold" data-id="element-275" data-mp="oeiixvrzefpp">
                  Stiahnuť
                </Button>
                <Button size="large" variant="secondary" onClick={t.reset} leftIcon={<RefreshCcwIcon className="w-4 h-4" data-id="element-278" data-mp="3mr1myfvia2vj" />} className="flex-1 !min-h-[48px] !rounded-full !font-semibold" data-id="element-277" data-mp="9n9lj0hu7q20">
                  Nová fotka
                </Button>
              </div>}

            {/* Next step after a result: Pro upsell */}
            {t.result && <div className="rounded-2xl border border-apple-blue/30 bg-blue-50/60 p-5 md:p-6" data-id="element-279" data-mp="38saeb8zex9es">
                <p className="text-base font-bold text-apple-black mb-3" data-id="element-280" data-mp="33spfcyk8t122">Páči sa ti návrh? Získaj ho v plnej kvalite.</p>
                <ul className="space-y-1.5 mb-5" data-id="element-281" data-mp="2z04l5vxfcly0">
                  {proPerks.map(perk => <li key={perk} className="flex items-center gap-2 text-sm text-apple-black" data-id="element-282" data-mp="2u3e353227g9y">
                      <CheckIcon className="w-4 h-4 text-apple-blue flex-shrink-0" aria-hidden="true" data-id="element-283" data-mp="2p4ug0zbqbyg0" />
                      {perk}
                    </li>)}
                </ul>
                <CtaLink href="#pro" className="w-full" data-id="element-284" data-mp="2g46204mc5y34">
                  Chcem Pro verziu
                </CtaLink>
              </div>}

            <ApiKeyPanel apiKey={t.apiKey} onApiKeyChange={t.setApiKey} data-id="element-285" data-mp="10jvot19ch5ie" />
          </div>
        </div>
      </div>
    </div>;
}